/**
 *
 * Attendance
 *
 */

import React, { memo, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Helmet } from 'react-helmet';
import { createStructuredSelector } from 'reselect';
import { compose } from 'redux';
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import moment from "utils/moment/momentLocale";

// styles
import './style.scss';

import { useInjectSaga } from 'utils/injectSaga';
import { useInjectReducer } from 'utils/injectReducer';
import reducer from './reducer';
import saga from './saga';

import {
  flushStateAttendance,
  loadMobileCourses,
} from './actions';

import {
  makeSelectLoading,
  makeSelectError,
  makeSelectItemsList,
} from './selectors';

// antd component
import { Layout } from 'antd';
const { Content } = Layout;

// components
import TCFCalendar from 'components/TCFCalendar';
// inner components
import { PresencesListCourses } from './components';
// containers
import AttendanceControl from "mobile/pages/AttendanceControl";

// utils
import { DAY_OF_WEEK_OPTIONS } from "utils/helpers/helper.constant";
import { getObjId } from "utils/helpers/helper.general";
import _local from "utils/localStorageUtils";


export function Attendance({
  // main data
  itemsList,
  // states
  history,
  loading,
  // actions
  loadMobileCourses,
  flushState,
}) {
  useInjectReducer({ key: "attendance", reducer });
  useInjectSaga({ key: "attendance", saga });

  const { t, i18n } = useTranslation();

  const now = moment();
  const week = now.isoWeekday();

  const localUser = _local.getLocalUser();
  const location = useLocation();

  const onDate = location?.state?.onDate;
  const timeZone = location?.state?.timeZone;

  const getDaysWeek = useCallback(() => DAY_OF_WEEK_OPTIONS(), [i18n?.language]);

  let initOptions = {
    _id: localUser?._id,
    course_week: getDaysWeek()[week - 1]?.value,
    onDate: now.toISOString(),
    timeZone: now.utcOffset(),
  };

  const [showFullCalendar, setShowFullCalendar] = useState(false);
  const [selectedDate, setSelectedDate] = useState(
    !!onDate && !location?.state?.needRefresh ? moment(onDate) : now,
  );
  const [optionsData, setOptionsData] = useState(initOptions);
  const [currentCourses, setCurrentCourses] = useState([]);
  // for AttendanceControl
  const [currentCourse, setCurrentCourse] = useState(false);
  const [checkedCourse, setCheckedCourse] = useState(false);

  // Clearing the state after unmounting a component
  // - work like ComponentDidUpdate
  useEffect(() => {
    itemsList?.length == 0 && loadMobileCourses(optionsData);

    if (!!location?.state?.needRefresh) {
      history.replace({
        ...history.location, // INFO: `...location` it buggy, and we will always be on the same page with this
        state: {
          ...location?.state,
          needRefresh: false,
          course_week: getDaysWeek()[week - 1]?.value,
          onDate: now.toISOString(),
          timeZone: now.utcOffset(),
        },
      });
    }

    return () => {
      // Anything in here is fired on component unmount.
      flushState();
      setCurrentCourses([]);
      setSelectedDate(null);
      setOptionsData(initOptions);
      // reset location statest for current component if pathname is not includes an "agenda" pathname
      if (!location?.pathname?.includes("agenda")) {
        history.replace({
          ...history.location, // INFO: `...location` it buggy, and we will always be on the same page with this
          state: {
            ...location?.state,
            course_week: getDaysWeek()[week - 1]?.value,
            onDate: now.toISOString(),
            timeZone: now.utcOffset(),
          },
        });
      }
    };
  }, []);

  useEffect(() => {
    if (itemsList?.length > 0) {
      setCurrentCourses(itemsList);
    } else {
      setCurrentCourses([]);
    }
  }, [itemsList]);

  // Reload list based on location state of date
  useEffect(() => {
    if (!!onDate && !location?.state?.needRefresh) {
      const onDateObject = moment(onDate);
      const onDateWeekday = onDateObject.isoWeekday();
      setSelectedDate(onDateObject);
      changeLoadOptions({
        ...optionsData,
        course_week: getDaysWeek()[onDateWeekday - 1]?.value,
        onDate: onDateObject.toISOString(),
        timeZone: onDateObject.utcOffset(),
      });
    }
  }, [onDate]);

  // Loading data when optionsData is changed
  const changeLoadOptions = useCallback(
    newOption => {
      if (JSON.stringify(optionsData) != JSON.stringify(newOption)) {
        // if date is changed then refresh data
        setCurrentCourses([]);
        setCurrentCourse(false);
        setCheckedCourse(false);

        const options = {
          ...optionsData,
          ...newOption,
        };
        loadMobileCourses(options);
        setOptionsData(options);
      }
    },
    [optionsData],
  );

  const onCalendarSelect = useCallback(
    value => {
      const date = moment(value);
      setSelectedDate(date);
      // 1 - Monday, 7 - Sunday
      const week2 = value.isoWeekday();
      changeLoadOptions({
        ...optionsData,
        course_week: getDaysWeek()[week2 - 1]?.value,
        onDate: date.toISOString(),
        timeZone: date.utcOffset(),
      });
    },
    [optionsData, location, i18n?.language],
  );

  const updateCourseHandle = useCallback(
    courseData => {
      setCheckedCourse(courseData);
      setCurrentCourse({
        ...currentCourse,
        ...courseData,
      });
      if (!!courseData) {
        const updatedCourses = currentCourses?.map(uc => {
          if (getObjId(uc) == getObjId(courseData)) {
            return { ...uc, ...courseData };
          } else {
            return uc;
          }
        });
        setCurrentCourses(updatedCourses);
      }
    },
    [currentCourses],
  );

  const checkCourse = useCallback(checkedCourseID => {
    setCheckedCourse(checkedCourseID);
  }, []);

  // render function
  return (
    <Layout className="main-full-content">
      <Helmet>
        <title>{t("labels.attendance")}</title>
        <meta name="description" content="Description of Attendance" />
      </Helmet>
      <Content
        className="main-content-container attendance-page"
        onClick={() => {
          !!showFullCalendar && setShowFullCalendar(false);
        }}
      >
        {!!showFullCalendar && (
          <TCFCalendar
            className="helper-calendar"
            valueState={selectedDate}
            onSelect={onCalendarSelect}
            onToggleFullView={() => {
              setShowFullCalendar(!showFullCalendar);
            }}
          />
        )}
        <TCFCalendar
          valueState={selectedDate}
          onSelect={onCalendarSelect}
          mode="day"
          onToggleFullView={() => {
            setShowFullCalendar(!showFullCalendar);
          }}
        />
        <div className="attendance-page__main">
          <PresencesListCourses
            className="courses-list-wrapper"
            selectedDate={moment(selectedDate).toISOString()}
            dataSourse={currentCourses}
            loading={loading}
            checkedCourse={checkedCourse}
            setCheckedCourse={setCheckedCourse}
            currentCourse={currentCourse}
            getCourse={checkCourse}
          />

          {!loading && (currentCourses && currentCourses.length > 0) && (
            <AttendanceControl
              courseID={getObjId(checkedCourse)}
              onDate={moment(selectedDate).toISOString()}
              timeZone={moment(selectedDate).utcOffset()}
              updateCourse={updateCourseHandle}
            />
          )}
        </div>
      </Content>
    </Layout>
  );
}

Attendance.propTypes = {
  dispatch: PropTypes.func.isRequired,
  flushState: PropTypes.func.isRequired,
  loading: PropTypes.bool,
};

const mapStateToProps = createStructuredSelector({
  loading: makeSelectLoading(),
  error: makeSelectError(),
  itemsList: makeSelectItemsList(),
});

function mapDispatchToProps(dispatch) {
  return {
    flushState: () => dispatch(flushStateAttendance()),
    loadMobileCourses: options => dispatch(loadMobileCourses(options)),
    dispatch,
  };
}

const withConnect = connect(
  mapStateToProps,
  mapDispatchToProps,
);

export default compose(
  withConnect,
  memo
)(Attendance);
